import type { AuthResponse } from "@/types/auth";

import { apiClient, apiPublicClient } from "../client";
import type { AuthMetadata } from "./types";

async function register(
  email: string,
  password: string,
  metadata: AuthMetadata,
  accepts_marketing: boolean,
): Promise<AuthResponse> {
  const { data, response } = await apiPublicClient.POST("/auth/register", {
    body: { email, password, metadata, accepts_marketing },
  });
  if (response.status === 409) throw new Error("Email already taken");
  if (!response.ok || !data) throw new Error(`Registration failed: ${response.status}`);
  return data;
}

async function registerLite(
  email: string,
  userName: string,
  metadata: AuthMetadata,
  accepts_marketing: boolean,
): Promise<AuthResponse> {
  const { data, response } = await apiPublicClient.POST("/auth/register/lite", {
    body: {
      email,
      user_name: userName,
      metadata,
      accepts_marketing,
    },
  });
  if (response.status === 409) throw new Error("Email already taken");
  if (!response.ok || !data) throw new Error(`Registration failed: ${response.status}`);
  return data;
}

async function setPassword(password: string): Promise<void> {
  const { response } = await apiClient.POST("/auth/set-password", {
    body: { password },
  });
  if (response.status === 409) throw new Error("Password already set");
  if (!response.ok) throw new Error(`Failed to set password: ${response.status}`);
}

async function sendCredentials(password: string): Promise<void> {
  const { response } = await apiClient.POST("/auth/send-credentials", {
    body: { password },
  });
  if (response.status === 400) throw new Error("Password is incorrect");
  if (!response.ok) throw new Error(`Failed to send credentials: ${response.status}`);
}

async function login(email: string, password: string): Promise<AuthResponse> {
  const { data, response } = await apiPublicClient.POST("/auth/login", {
    body: { email, password },
  });
  if (!response.ok || !data) throw new Error("Invalid email or password");
  return data;
}

async function magicLogin(code: string): Promise<AuthResponse> {
  const { data, response } = await apiPublicClient.POST("/auth/magic-login", {
    body: { code },
  });
  if (!response.ok || !data) throw new Error("Magic login failed");
  return data;
}

async function loginWithGoogle(
  code: string,
  metadata: AuthMetadata,
  accepts_marketing: boolean,
): Promise<AuthResponse> {
  const { data, response } = await apiPublicClient.POST("/auth/google", {
    body: { code, metadata, accepts_marketing },
  });
  if (!response.ok || !data) throw new Error("Google sign-in failed");
  return data;
}

export const apiAuth = {
  register,
  registerLite,
  setPassword,
  sendCredentials,
  login,
  magicLogin,
  loginWithGoogle,
};
